import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { portalExpansionApi, Application, ApplicationRecipient } from '../lib/portal-expansion-api';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

export default function FundingApplicationsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['funding-applications', page, statusFilter],
    queryFn: () => portalExpansionApi.getAllApplications(page, 20, statusFilter || undefined),
    retry: 1,
  });

  const { data: selectedApplication } = useQuery({
    queryKey: ['application-detail', selectedApp],
    queryFn: () => portalExpansionApi.getApplication(selectedApp!),
    enabled: !!selectedApp,
    retry: 1,
  });

  if (error) {
    return (
      <div className="error-state">
        <h2>Unable to load funding applications</h2>
        <p>
          {(error as any)?.response?.status === 404
            ? 'This endpoint is not yet implemented in the backend.'
            : 'An error occurred loading the funding applications queue.'}
        </p>
        <button onClick={() => window.location.reload()} className="btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading funding applications...</p>
      </div>
    );
  }

  const applications = data?.applications || [];
  const total = data?.total || 0;

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="pagehead between">
        <div>
          <h1>Funding Applications</h1>
          <p>Review program applications and manage recipient approvals</p>
        </div>
        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="">All statuses</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="action_required">Action Required</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="referred_to_finance">Referred to Finance</option>
            <option value="payment_processing">Payment Processing</option>
            <option value="payment_processed">Payment Processed</option>
          </select>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="empty-state">
          <svg
            className="icon-large"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3>No applications found</h3>
          <p>
            {statusFilter
              ? `No applications with status "${statusFilter.replace(/_/g, ' ')}".`
              : 'There are no funding applications submitted yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="applications-grid">
            {applications.map((app: Application) => (
              <ApplicationCard
                key={app.id}
                application={app}
                onSelect={() => setSelectedApp(app.id)}
              />
            ))}
          </div>

          {total > 20 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span>
                Page {page} of {Math.ceil(total / 20)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedApp && selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          onClose={() => setSelectedApp(null)}
          queryClient={queryClient}
          showToast={showToast}
        />
      )}

      <style>{`
        .pagehead {
          margin-bottom: 25px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
        }

        .pagehead.between {
          flex-wrap: wrap;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #e6e9de;
          border-radius: 8px;
          font-size: 13px;
          background: white;
          color: #424a47;
          cursor: pointer;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .error-state h2 {
          font-size: 18px;
          margin-bottom: 10px;
          color: #d14343;
        }

        .error-state p,
        .loading-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .icon-large {
          width: 48px;
          height: 48px;
          color: #68716d;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          color: #68716d;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #68716d;
        }

        .applications-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .btn,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:hover,
        .btn-secondary:hover {
          background: #f6f6f3;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 20px;
        }

        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .applications-grid {
            grid-template-columns: 1fr;
          }

          .pagehead {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </>
  );
}

function ApplicationCard({
  application,
  onSelect,
}: {
  application: Application;
  onSelect: () => void;
}) {
  const recipientCount = application.recipients?.length || 0;
  const approvedCount =
    application.recipients?.filter((r) => r.status === 'approved').length || 0;

  return (
    <div className="app-card" onClick={onSelect}>
      <div className="app-card-header">
        <div>
          <h3>{application.program.name}</h3>
          <p className="app-ref">Ref: {application.referenceNumber}</p>
        </div>
        <StatusBadge status={application.status} />
      </div>

      <div className="app-card-body">
        <div className="app-info-row">
          <span className="label">Recipients:</span>
          <span className="value">
            {approvedCount} / {recipientCount}
          </span>
        </div>
        <div className="app-info-row">
          <span className="label">Submitted:</span>
          <span className="value">
            {application.submittedAt
              ? new Date(application.submittedAt).toLocaleDateString()
              : '—'}
          </span>
        </div>
        <div className="app-info-row">
          <span className="label">Last Updated:</span>
          <span className="value">{new Date(application.updatedAt).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="app-card-footer">
        <button className="review-btn">Review Application →</button>
      </div>

      <style>{`
        .app-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s;
        }

        .app-card:hover {
          border-color: #4b607f;
          box-shadow: 0 4px 12px rgba(75, 96, 127, 0.1);
        }

        .app-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          gap: 12px;
        }

        .app-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: #313739;
          margin-bottom: 4px;
        }

        .app-ref {
          font-size: 11px;
          color: #68716d;
          font-family: 'SF Mono', Consolas, monospace;
        }

        .app-card-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f2f3ee;
        }

        .app-info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
        }

        .app-info-row .label {
          color: #68716d;
        }

        .app-info-row .value {
          color: #313739;
          font-weight: 500;
        }

        .app-card-footer {
          display: flex;
          justify-content: flex-end;
        }

        .review-btn {
          font-size: 12px;
          color: #4b607f;
          font-weight: 500;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
        }

        .review-btn:hover {
          color: #3d4f68;
        }
      `}</style>
    </div>
  );
}

function ApplicationDetailModal({
  application,
  onClose,
  queryClient,
  showToast,
}: {
  application: Application;
  onClose: () => void;
  queryClient: any;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [selectedRecipient, setSelectedRecipient] = useState<ApplicationRecipient | null>(null);
  const [showRequestInfo, setShowRequestInfo] = useState(false);

  const _updateStatusMutation = useMutation({
    mutationFn: (payload: { status: string; publicMessage?: string; internalNotes?: string }) =>
      portalExpansionApi.updateApplicationStatus(application.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding-applications'] });
      queryClient.invalidateQueries({ queryKey: ['application-detail'] });
      showToast('Application status updated', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const updateRecipientMutation = useMutation({
    mutationFn: (payload: { recipientId: string; status: string; paymentReference?: string; notes?: string }) =>
      portalExpansionApi.updateRecipientStatus(payload.recipientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding-applications'] });
      queryClient.invalidateQueries({ queryKey: ['application-detail'] });
      setSelectedRecipient(null);
      showToast('Recipient status updated', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const requestInfoMutation = useMutation({
    mutationFn: (payload: { description: string; requestedChange?: string; personId?: string }) =>
      portalExpansionApi.requestInformation(application.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding-applications'] });
      queryClient.invalidateQueries({ queryKey: ['application-detail'] });
      setShowRequestInfo(false);
      showToast('Information request sent to member', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-xlarge">
        <div className="modal-header">
          <div>
            <h2>{application.program.name}</h2>
            <p className="modal-subtitle">Reference: {application.referenceNumber}</p>
          </div>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="detail-section">
            <div className="section-header">
              <h3>Application Status</h3>
              <StatusBadge status={application.status} />
            </div>

            <div className="info-grid">
              <div>
                <label>Submitted</label>
                <p>
                  {application.submittedAt
                    ? new Date(application.submittedAt).toLocaleString()
                    : 'Draft'}
                </p>
              </div>
              <div>
                <label>Last Updated</label>
                <p>{new Date(application.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Recipients ({application.recipients.length})</h3>
            <div className="recipients-list">
              {application.recipients.map((recipient) => (
                <div key={recipient.id} className="recipient-row">
                  <div className="recipient-info">
                    <strong>
                      {recipient.person.legalFirstName} {recipient.person.legalLastName}
                    </strong>
                    <small>
                      Treaty: {recipient.person.treatyNumber} | DOB:{' '}
                      {new Date(recipient.person.dateOfBirth).toLocaleDateString()}
                    </small>
                  </div>
                  <div className="recipient-actions">
                    <StatusBadge status={recipient.status} />
                    <button
                      onClick={() => setSelectedRecipient(recipient)}
                      className="btn-small"
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {application.corrections && application.corrections.length > 0 && (
            <div className="detail-section">
              <h3>Correction Requests ({application.corrections.length})</h3>
              <div className="corrections-list">
                {application.corrections.map((correction) => (
                  <div key={correction.id} className="correction-item">
                    <div className="correction-header">
                      <StatusBadge status={correction.status} />
                      <span className="correction-date">
                        {new Date(correction.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="correction-desc">{correction.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
          <button onClick={() => setShowRequestInfo(true)} className="btn-secondary">
            Request Information
          </button>
        </div>
      </div>

      {showRequestInfo && (
        <RequestInformationModal
          applicationId={application.id}
          onClose={() => setShowRequestInfo(false)}
          onSubmit={(data) => requestInfoMutation.mutate(data)}
          isSubmitting={requestInfoMutation.isPending}
        />
      )}

      {selectedRecipient && (
        <RecipientReviewModal
          recipient={selectedRecipient}
          onClose={() => setSelectedRecipient(null)}
          onUpdate={(data) =>
            updateRecipientMutation.mutate({ recipientId: selectedRecipient.id, ...data })
          }
          isSubmitting={updateRecipientMutation.isPending}
        />
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 100;
        }

        .modal-xlarge {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 12px;
          width: 95%;
          max-width: 900px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          z-index: 101;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #f2f3ee;
        }

        .modal-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: #313739;
        }

        .modal-subtitle {
          font-size: 12px;
          color: #68716d;
          margin-top: 4px;
        }

        .modal-close {
          font-size: 28px;
          line-height: 1;
          color: #68716d;
          background: none;
          border: 0;
          padding: 0;
          width: 32px;
          height: 32px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .detail-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid #f2f3ee;
        }

        .detail-section:last-child {
          border-bottom: 0;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .detail-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: #313739;
          margin-bottom: 12px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .info-grid label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #68716d;
          margin-bottom: 4px;
        }

        .info-grid p {
          font-size: 13px;
          color: #313739;
        }

        .recipients-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .recipient-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          gap: 12px;
        }

        .recipient-info {
          flex: 1;
          min-width: 0;
        }

        .recipient-info strong {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #313739;
        }

        .recipient-info small {
          display: block;
          font-size: 11px;
          color: #68716d;
          margin-top: 2px;
        }

        .recipient-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
          border-radius: 8px;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
        }

        .btn-small:hover {
          background: #f6f6f3;
        }

        .corrections-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .correction-item {
          padding: 12px;
          background: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 8px;
        }

        .correction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .correction-date {
          font-size: 11px;
          color: #92400e;
        }

        .correction-desc {
          font-size: 12px;
          color: #92400e;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #f2f3ee;
          justify-content: flex-end;
        }

        .btn-secondary {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          text-decoration: none;
        }

        .btn-secondary:hover {
          background: #f6f6f3;
        }

        @media (max-width: 640px) {
          .modal-xlarge {
            width: 100%;
            height: 100%;
            max-width: none;
            max-height: none;
            border-radius: 0;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .recipient-row {
            flex-direction: column;
            align-items: flex-start;
          }

          .recipient-actions {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </>
  );
}

function RecipientReviewModal({
  recipient,
  onClose,
  onUpdate,
  isSubmitting,
}: {
  recipient: ApplicationRecipient;
  onClose: () => void;
  onUpdate: (data: any) => void;
  isSubmitting: boolean;
}) {
  const [status, setStatus] = useState(recipient.status);
  const [paymentReference, setPaymentReference] = useState(recipient.paymentReference || '');
  const [notes, setNotes] = useState('');

  return (
    <>
      <div className="modal-overlay-top" onClick={onClose} />
      <div className="modal-small">
        <div className="modal-header">
          <h2>Review Recipient</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="recipient-summary">
            <strong>
              {recipient.person.legalFirstName} {recipient.person.legalLastName}
            </strong>
            <p>Treaty: {recipient.person.treatyNumber}</p>
            <p>DOB: {new Date(recipient.person.dateOfBirth).toLocaleDateString()}</p>
          </div>

          <div className="form-group">
            <label>Decision *</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="payment_hold">Payment Hold</option>
            </select>
          </div>

          {status === 'approved' && (
            <div className="form-group">
              <label>Payment Reference</label>
              <input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Finance reference number"
                disabled={isSubmitting}
              />
              <small>Evidence of payment sent (not received by member's bank)</small>
            </div>
          )}

          <div className="form-group">
            <label>Admin Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes (not visible to member)"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            onClick={() => onUpdate({ status, paymentReference, notes })}
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Decision'}
          </button>
        </div>

        <style>{`
          .modal-overlay-top {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 200;
          }

          .modal-small {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            border-radius: 12px;
            width: 90%;
            max-width: 500px;
            max-height: 80vh;
            display: flex;
            flex-direction: column;
            z-index: 201;
          }

          .recipient-summary {
            padding: 12px;
            background: #f9fafb;
            border-radius: 8px;
            margin-bottom: 16px;
          }

          .recipient-summary strong {
            display: block;
            font-size: 14px;
            color: #313739;
            margin-bottom: 4px;
          }

          .recipient-summary p {
            font-size: 12px;
            color: #68716d;
            margin: 2px 0;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-group select,
          .form-group input,
          .form-group textarea {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 13px;
            color: #1f2937;
          }

          .form-group select:focus,
          .form-group input:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #4b607f;
          }

          .form-group select:disabled,
          .form-group input:disabled,
          .form-group textarea:disabled {
            background: #f3f4f6;
            cursor: not-allowed;
          }

          .form-group small {
            display: block;
            font-size: 11px;
            color: #6b7280;
            margin-top: 4px;
          }

          .btn-primary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 0;
            background: #4b607f;
            color: white;
            cursor: pointer;
          }

          .btn-primary:hover:not(:disabled) {
            background: #3d4f68;
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    </>
  );
}

function RequestInformationModal({
  applicationId: _applicationId,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  applicationId: string;
  onClose: () => void;
  onSubmit: (data: { description: string; requestedChange?: string; personId?: string }) => void;
  isSubmitting: boolean;
}) {
  const [description, setDescription] = useState('');
  const [requestedChange, setRequestedChange] = useState('');

  return (
    <>
      <div className="modal-overlay-top" onClick={onClose} />
      <div className="modal-small">
        <div className="modal-header">
          <h2>Request Information from Member</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: '13px', color: '#68716d', marginBottom: '16px' }}>
            This creates a public correction request visible to the member. They will be notified and can respond.
          </p>

          <div className="form-group">
            <label>Public Message to Member *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Explain what information is needed..."
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Requested Change (optional)</label>
            <textarea
              value={requestedChange}
              onChange={(e) => setRequestedChange(e.target.value)}
              placeholder="Specific change needed, if applicable"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            onClick={() => onSubmit({ description, requestedChange: requestedChange || undefined })}
            className="btn-primary"
            disabled={isSubmitting || !description.trim()}
          >
            {isSubmitting ? 'Sending...' : 'Send Request'}
          </button>
        </div>
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: '#68716d',
    submitted: '#3b82f6',
    under_review: '#f59e0b',
    action_required: '#f59e0b',
    approved: '#10b981',
    rejected: '#ef4444',
    referred_to_finance: '#8b5cf6',
    payment_processing: '#06b6d4',
    payment_processed: '#10b981',
    pending: '#68716d',
    payment_hold: '#f59e0b',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 500,
        background: `${colors[status] || '#68716d'}15`,
        color: colors[status] || '#68716d',
        whiteSpace: 'nowrap',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}
